import { useEffect, useState, type ReactNode } from 'react';
import { InfoIcon } from '@phosphor-icons/react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@voll-intelligence/ui-kit/ui/tooltip';
import type { HotelAsyncState, HotelTravelerOption } from '../models.js';
import type { PersonItem } from '../../shared/person/person_row.js';
import {
  PickerHeader,
  TravelerPicker,
  type TravelerPickerCopy,
  type TravelerSearchState,
} from '../../shared/person/traveler_picker.js';
import { TravelerPolicyRow } from '../../shared/person/traveler_picker_footer.js';
import type { Choosing, HotelRoomsFieldCopy } from './hotel_rooms_field.js';
import type { RoomState } from './hotel_rooms_matrix.js';
import { findTraveler } from './hotel_rooms_travelers.js';

/*
 * As telas de ESCOLHA do campo QUEM do hotel — hóspede de uma vaga, sujeito da
 * política, e o aviso do nome da reserva — separadas da matriz de quartos
 * (`hotel_rooms_field.tsx`) pelo teto de linhas dela. Todas sobre o
 * `TravelerPicker` compartilhado com o aéreo.
 */

export function toPerson(traveler: HotelTravelerOption): PersonItem {
  return {
    id: traveler.id,
    name: traveler.label,
    ...(traveler.description ? { description: traveler.description } : {}),
    ...(traveler.photoUrl ? { photoUrl: traveler.photoUrl } : {}),
  };
}

/** A copy do picker compartilhado a partir da do campo. Piso e teto não se
 *  aplicam ao modo único; as legendas ficam vazias e nunca aparecem. */
function pickerCopy(copy: HotelRoomsFieldCopy): TravelerPickerCopy {
  return {
    label: copy.travelersLabel,
    searchPlaceholder: copy.travelerSearchPlaceholder,
    empty: copy.noTravelersLabel,
    error: copy.travelerErrorLabel,
    loadMore: copy.loadMoreTravelersLabel,
    you: copy.policyRequesterLabel,
    youRole: copy.guestRoleLabel,
    selectedGroup: '',
    maxReached: '',
    minReached: '',
  };
}

function pickerState(state: HotelAsyncState): TravelerSearchState {
  return state === 'idle' ? 'ready' : state;
}

/** Quem já está em algum quarto: id → nº do quarto (1-based). */
function placedRooms(matrix: readonly RoomState[]): Map<string, number> {
  const placed = new Map<string, number>();
  matrix.forEach((room, index) => {
    for (const { travelerId } of room.slots) if (travelerId) placed.set(travelerId, index + 1);
  });
  return placed;
}

/** A lista de pessoas para uma VAGA: a busca do diretório em modo único. Quem
 *  já está em outro quarto fica visível e travado com o motivo (quando o host
 *  tem a copy), em vez de sumir da lista sem explicação. */
export function GuestPicker({
  choosing,
  matrix,
  travelers,
  travelerSearchState,
  hasMoreTravelers,
  copy,
  fill,
  requesterId,
  query,
  onQueryChange,
  onBack,
  onPick,
  onLoadMoreTravelers,
}: {
  readonly choosing: Choosing;
  readonly matrix: readonly RoomState[];
  readonly travelers: readonly HotelTravelerOption[];
  readonly travelerSearchState: HotelAsyncState;
  readonly hasMoreTravelers: boolean;
  readonly copy: HotelRoomsFieldCopy;
  readonly fill: boolean;
  /** O solicitante, fixado no topo como "Você" quando a busca o conhece. */
  readonly requesterId: string | undefined;
  readonly query: string;
  readonly onQueryChange: (query: string) => void;
  readonly onBack: () => void;
  readonly onPick: (id: string) => void;
  readonly onLoadMoreTravelers: () => void;
}): ReactNode {
  const placed = placedRooms(matrix);
  const inRoom = copy.guestInRoomLabel;
  // Na falha da busca o host ainda segura a última lista; aqui ela sai de cena
  // para o alerta aparecer no lugar (a régua do picker: erro só sem linhas).
  const items =
    travelerSearchState === 'error'
      ? []
      : travelers.filter((traveler) => inRoom !== undefined || !placed.has(traveler.id));
  const disabled: Record<string, string> = {};
  if (inRoom) {
    for (const [id, room] of placed) disabled[id] = inRoom(room);
  }
  // Fora da lista quando ela esconde quem já está num quarto (host sem a legenda).
  const requester =
    requesterId && (inRoom !== undefined || !placed.has(requesterId))
      ? findTraveler(requesterId, travelers)
      : undefined;
  return (
    <TravelerPicker
      mode="single"
      items={items.map(toPerson)}
      selected={[]}
      {...(requester ? { pinned: toPerson(requester) } : {})}
      disabled={disabled}
      query={query}
      searchState={pickerState(travelerSearchState)}
      hasMore={hasMoreTravelers}
      copy={pickerCopy(copy)}
      fill={fill}
      header={
        <PickerHeader
          title={copy.roomGuestHeading(choosing.room + 1, choosing.slot > 0)}
          backLabel={copy.backToRoomsLabel}
          onBack={onBack}
        />
      }
      onQueryChange={onQueryChange}
      onLoadMore={onLoadMoreTravelers}
      onSelect={(person) => {
        onPick(person.id);
      }}
    />
  );
}

/** A lista de política: os hóspedes nomeados em modo único, a mesma linguagem
 *  da escolha de hóspede — não um `Select` em camada por cima. */
export function PolicyPicker({
  namedIds,
  policyId,
  travelers,
  copy,
  fill,
  onBack,
  onPick,
}: {
  readonly namedIds: readonly string[];
  readonly policyId: string | undefined;
  readonly travelers: readonly HotelTravelerOption[];
  readonly copy: HotelRoomsFieldCopy;
  readonly fill: boolean;
  readonly onBack: () => void;
  readonly onPick: (id: string) => void;
}): ReactNode {
  const people = namedIds.map((id) => {
    const traveler = findTraveler(id, travelers);
    return traveler ? toPerson(traveler) : { id, name: id };
  });
  const current = people.find((person) => person.id === policyId);
  return (
    <TravelerPicker
      mode="single"
      items={people}
      selected={current ? [current] : []}
      searchable={false}
      query=""
      searchState="ready"
      hasMore={false}
      copy={pickerCopy(copy)}
      fill={fill}
      header={
        <PickerHeader
          title={copy.policyTravelerLabel}
          backLabel={copy.backToRoomsLabel}
          onBack={onBack}
        />
      }
      onQueryChange={() => {}}
      onSelect={(person) => {
        onPick(person.id);
      }}
    />
  );
}

/** A linha da política no pé da visão geral, com o recuo do painel. Só existe
 *  com 2+ hóspedes nomeados. */
export function PolicyRow({
  policyId,
  requesterId,
  travelers,
  copy,
  disabled,
  onChoose,
}: {
  readonly policyId: string;
  readonly requesterId: string | undefined;
  readonly travelers: readonly HotelTravelerOption[];
  readonly copy: HotelRoomsFieldCopy;
  readonly disabled: boolean;
  readonly onChoose: () => void;
}): ReactNode {
  const traveler = findTraveler(policyId, travelers);
  return (
    <div className="px-3 py-2">
      <TravelerPolicyRow
        subject={traveler ? toPerson(traveler) : { id: policyId, name: policyId }}
        requesterId={requesterId}
        label={copy.policyTravelerLabel}
        youLabel={copy.policyRequesterLabel}
        disabled={disabled}
        onChoose={onChoose}
      />
    </div>
  );
}

/**
 * O aviso "a reserva fica no nome do primeiro hóspede" como ícone com tooltip,
 * ao lado do rótulo do quarto. Passar o mouse mostra; o CLIQUE trava (o toque
 * não tem hover), e qualquer toque fora do ícone solta — a leitura acontece
 * antes de o dedo chegar em outro controle, e o clique seguinte já vale.
 */
export function RoomNote({ note }: { readonly note: string }): ReactNode {
  const [pinned, setPinned] = useState(false);
  const [hovered, setHovered] = useState(false);
  useEffect(() => {
    if (!pinned) return undefined;
    const onPointerDown = (event: PointerEvent) => {
      if (event.target instanceof Element && event.target.closest('[data-room-note]')) return;
      setPinned(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [pinned]);
  return (
    <Tooltip open={pinned || hovered} onOpenChange={setHovered}>
      <TooltipTrigger
        render={
          <button
            type="button"
            data-room-note=""
            className={`inline-flex size-5 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground ${
              pinned ? 'bg-muted text-foreground' : ''
            }`}
            aria-label={note}
            aria-expanded={pinned}
            onClick={() => {
              setPinned((current) => !current);
            }}
          />
        }
      >
        <InfoIcon className="size-4" aria-hidden="true" />
      </TooltipTrigger>
      <TooltipContent>{note}</TooltipContent>
    </Tooltip>
  );
}
